import { useMemo } from "react";

import { useDigitalSizerClient } from "./useDigitalSizerApi";

// Role values used by the eastview-os B2BUser model (roles[] is multi-valued).
// Best-effort list for the role filter + edit/create pickers; the backend stores
// whatever is sent, so this is a convenience list, not an enforced enum.
export const ROLE_OPTIONS = [
  "loan_officer",
  "underwriter",
  "preUnderwriter",
  "qc_analyst",
  "operations",
  "processor",
  "sales_admin",
  "admin",
];

// Display names for role values whose raw string is not self-explanatory.
// The VALUE is what the backend stores and matches on — only the label differs.
export const ROLE_LABELS = {
  loan_officer: "Loan Officer",
  underwriter: "Underwriter",
  preUnderwriter: "Junior Underwriter",
  qc_analyst: "QC Review",
  operations: "Operations",
  processor: "Processor",
  sales_admin: "Sales Admin",
  admin: "Admin",
};

// Label for a role value; unknown / free-typed roles fall back to the raw value.
export const roleLabel = (role) => ROLE_LABELS[role] || role || "";

// User CRUD on eastview-os's /users router, via the LOS client (dsHttp →
// eastview-los). MUST go through this client: the legacy useUsersHook (useAxiosHook
// default domain) hits the old RTL backend, which 403s under the JWT auth swap, so
// /users/all_users never fires there. dsHttp is the host that returns 200.
export const useUsersApi = () => {
  const client = useDigitalSizerClient();

  return useMemo(() => {
    const get = async (p) => (await client.get(p)).data;
    const post = async (p, b) => (await client.post(p, b)).data;
    return {
      // GET /users/all_users → array of B2BUser docs (every current user).
      listAllUsers: () => get("/users/all_users"),
      // GET /users/logged_in_user → the caller's own B2BUser doc.
      getMyProfile: () => get("/users/logged_in_user"),
      // POST /users/create_user → { organization, user }. Domain-gated (403 if disallowed).
      createUser: (body) => post("/users/create_user", body),
      // POST /users/update_user_by_id/:id → updated user ($set of sent fields).
      updateUser: (id, body) => post(`/users/update_user_by_id/${id}`, body),
    };
  }, [client]);
};

// Admin actions served by eastview-os's /auth router (LOS client / dsHttp). Only
// the routes that actually exist are exposed here — there is no delete or
// standalone revoke-sessions route. See app/routers/auth.py.
export const useAdminUsersApi = () => {
  const client = useDigitalSizerClient();

  return useMemo(() => {
    const get = async (p) => (await client.get(p)).data;
    const post = async (p, b) => (await client.post(p, b)).data;

    return {
      // POST /auth/admin/impersonate — ImpersonateRequest { target_user_id, reason }.
      // Returns a short-lived act-as token. (Not applied client-side in this app —
      // there's no session-swap surface; we still issue it to mirror the LOS.)
      impersonate: (userId, reason = "admin sudo from users screen") =>
        post("/auth/admin/impersonate", {
          target_user_id: userId,
          reason,
        }),

      // POST /auth/admin/users/:id/set-password — SetPasswordRequest { new_password }.
      // Also revokes the target's live sessions (forces re-login).
      setPassword: (userId, newPassword) =>
        post(`/auth/admin/users/${userId}/set-password`, {
          new_password: newPassword,
        }),

      // GET /auth/admin/users/:id/activity — recent sign-in / auth events for one user.
      userActivity: (userId) => get(`/auth/admin/users/${userId}/activity`),
    };
  }, [client]);
};

// "Who's online" — currently-open sessions joined with each user's last
// activity, served by eastview-os via the LOS client (dsHttp). See
// app/routers/auth.py → GET /auth/admin/active_sessions, which returns
// { count, sessions: [{ userEmail, name, roles, lastActivityAt, lastEvent,
// lastPath, ipAddress, issuedAt, expiresAt, ... }] }.
export const useActiveSessionsApi = () => {
  const client = useDigitalSizerClient();

  return useMemo(() => {
    const get = async (p) => (await client.get(p)).data;
    return {
      activeSessions: () => get("/auth/admin/active_sessions"),
    };
  }, [client]);
};

export default useAdminUsersApi;
